<template>
  <div>
    <!-- Page Header -->
    <CCard class="mb-4">
      <CCardBody class="p-4">
        <div class="d-flex flex-wrap align-items-center justify-content-between">
          <div class="mb-2 mb-md-0">
            <h1 class="h4 mb-1 font-weight-bold">Quotations</h1>
            <p class="text-muted mb-0">
              Manage your sales quotations and track which ones are accepted
            </p>
          </div>
          <div class="d-flex align-items-center">
            <CButton
              color="light"
              class="mr-2"
              aria-label="Refresh"
              title="Refresh"
              :disabled="loading"
              @click="refreshAll"
            >
              <CIcon name="cil-reload" />
            </CButton>
            <CButton color="primary" @click="addNew">
              <CIcon name="cil-plus" /> Create quotation
            </CButton>
          </div>
        </div>
      </CCardBody>
    </CCard>

    <!-- Quotation Insights -->
    <CRow class="mb-4">
      <!-- Quotation Summary -->
      <CCol lg="8" class="mb-4 mb-lg-0">
        <CCard class="h-100">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-1">Quotation summary</h2>
            <div class="small text-muted">All quotations, with the last 6 months below</div>
          </CCardHeader>
          <CCardBody class="pt-0 pb-4 px-4">
            <CRow class="mb-4">
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Total quotations</span>
                    <span class="inv-kpi-chip chip-navy"><CIcon name="cil-file" /></span>
                  </div>
                  <div class="inv-kpi-value">{{ quotationSummary.totalCount }}</div>
                  <div class="inv-kpi-sub" :class="quotationSummary.countChange >= 0 ? 'trend-up' : 'trend-down'">
                    <CIcon :name="quotationSummary.countChange >= 0 ? 'cil-arrow-top' : 'cil-arrow-bottom'" />
                    {{ Math.abs(quotationSummary.countChange) }}% vs last month
                  </div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Accepted</span>
                    <span class="inv-kpi-chip chip-success"><CIcon name="cil-check-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">RM {{ formatMyr(quotationSummary.acceptedAmount) }}</div>
                  <div class="inv-kpi-sub">{{ quotationSummary.acceptedCount }} quotation{{ quotationSummary.acceptedCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3" class="mb-3 mb-md-0">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Sent, awaiting reply</span>
                    <span class="inv-kpi-chip chip-warning"><CIcon name="cil-clock" /></span>
                  </div>
                  <div class="inv-kpi-value">RM {{ formatMyr(quotationSummary.pendingAmount) }}</div>
                  <div class="inv-kpi-sub">{{ quotationSummary.pendingCount }} quotation{{ quotationSummary.pendingCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
              <CCol sm="6" md="3">
                <div class="inv-kpi">
                  <div class="inv-kpi-top">
                    <span class="inv-kpi-label">Rejected</span>
                    <span class="inv-kpi-chip chip-danger"><CIcon name="cil-x-circle" /></span>
                  </div>
                  <div class="inv-kpi-value">RM {{ formatMyr(quotationSummary.rejectedAmount) }}</div>
                  <div class="inv-kpi-sub">{{ quotationSummary.rejectedCount }} quotation{{ quotationSummary.rejectedCount === 1 ? '' : 's' }}</div>
                </div>
              </CCol>
            </CRow>
            <QuotationTrendChart
              :chartData="quotationTrendData"
              style="height: 250px"
            />
          </CCardBody>
        </CCard>
      </CCol>
      <!-- Status Distribution -->
      <CCol lg="4">
        <CCard class="h-100">
          <CCardHeader class="bg-transparent border-0 p-4">
            <h2 class="h6 font-weight-bold mb-1">Status distribution</h2>
            <div class="small text-muted">All quotations by status</div>
          </CCardHeader>
          <CCardBody
            class="pt-0 pb-4 px-4 d-flex flex-column align-items-center justify-content-center"
          >
            <div style="height: 200px; width: 200px" class="mb-4">
              <QuotationStatusChart :chartData="statusDistributionData" />
            </div>
            <div class="w-100">
              <div
                v-for="status in statusDistribution"
                :key="status.name"
                class="d-flex align-items-center mb-2"
              >
                <span
                  class="status-dot mr-2"
                  :class="`bg-${badgeColor('quotation', status.name)}`"
                ></span>
                <div class="flex-grow-1">{{ badgeLabel("quotation", status.name) }}</div>
                <div class="text-right">
                  <strong>{{ status.count }}</strong>
                  <span class="text-muted ml-1">({{ status.percentage }}%)</span>
                </div>
              </div>
            </div>
          </CCardBody>
        </CCard>
      </CCol>
    </CRow>

    <!-- Quotation List -->
    <CCard class="mb-4">
      <CCardHeader class="bg-transparent border-0 p-4">
        <h2 class="h6 font-weight-bold mb-0">
          All quotations
          <CBadge color="light" class="ml-2">{{ totalItems }}</CBadge>
        </h2>
      </CCardHeader>
      <CCardBody class="p-4">
        <!-- Filters -->
        <CCollapse :show="showFilters">
          <CCard class="mb-4 border">
            <CCardBody>
              <CRow>
                <CCol md="3">
                  <CInput
                    label="Quotation number"
                    v-model="filters.quotationNumber"
                    placeholder="Search quotation number"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CInput
                    label="Customer"
                    v-model="filters.customerName"
                    placeholder="Search customer"
                    @keyup.enter="applyFilters"
                  />
                </CCol>
                <CCol md="3">
                  <CSelect
                    label="Status"
                    v-model="filters.status"
                    @change="applyFilters"
                  >
                    <option value="">All statuses</option>
                    <option
                      v-for="status in statusOptions"
                      :key="status.id"
                      :value="status.name"
                    >
                      {{ badgeLabel("quotation", status.name) }}
                    </option>
                  </CSelect>
                </CCol>
                <CCol md="3">
                  <CInput
                    type="date"
                    label="From"
                    v-model="filters.dateFrom"
                    class="mb-3"
                    @change="applyFilters"
                  />
                  <CInput
                    type="date"
                    label="To"
                    v-model="filters.dateTo"
                    :is-valid="dateRangeInvalid ? false : null"
                    invalid-feedback="The To date must be on or after the From date."
                    @change="applyFilters"
                  />
                </CCol>
              </CRow>
              <CRow class="mt-3">
                <CCol class="d-flex justify-content-end">
                  <CButton color="light" class="mr-2" @click="resetFilters">
                    <CIcon name="cil-x" /> Clear filters
                  </CButton>
                  <CButton color="primary" @click="applyFilters">
                    <CIcon name="cil-filter" /> Apply filters
                  </CButton>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>
        </CCollapse>

        <div class="d-flex flex-wrap align-items-center mb-4">
          <CButton color="light" @click="showFilters = !showFilters">
            <CIcon :name="showFilters ? 'cil-chevron-top' : 'cil-filter'" />
            {{ showFilters ? "Hide filters" : "Show filters" }}
          </CButton>
          <CBadge v-if="hasActiveFilters" color="primary" class="ml-2">
            {{ activeFiltersCount }} active
          </CBadge>
          <CButton
            v-if="hasActiveFilters"
            color="link"
            class="ml-2"
            @click="resetFilters"
          >
            Clear filters
          </CButton>
        </div>

        <!-- Loading (first load) -->
        <div v-if="loading && !items.length" class="text-center py-5">
          <CSpinner color="primary" />
          <p class="mt-3 text-muted mb-0">Loading quotations…</p>
        </div>

        <!-- Error -->
        <div v-else-if="loadError" class="text-center py-5">
          <CIcon name="cil-warning" size="2xl" class="mb-3 text-danger" />
          <h3 class="h6 font-weight-bold">Couldn't load quotations</h3>
          <p class="text-muted mb-3">Check your connection and try again.</p>
          <CButton color="primary" @click="refreshTable">
            <CIcon name="cil-reload" /> Try again
          </CButton>
        </div>

        <!-- Empty (first use) -->
        <div v-else-if="!items.length && !hasActiveFilters" class="text-center py-5">
          <CIcon name="cil-description" size="2xl" class="mb-3 text-muted" />
          <h3 class="h6 font-weight-bold">No quotations yet</h3>
          <p class="text-muted mb-3">
            Send customers a price before they commit. Accepted quotations convert to invoices.
          </p>
          <CButton color="primary" @click="addNew">
            <CIcon name="cil-plus" /> Create quotation
          </CButton>
        </div>

        <!-- Empty (filtered) -->
        <div v-else-if="!items.length" class="text-center py-4">
          <h3 class="h6 font-weight-bold">No quotations match your filters</h3>
          <CButton color="light" class="mt-2" @click="resetFilters">
            <CIcon name="cil-x" /> Clear filters
          </CButton>
        </div>

        <template v-else>
          <div v-if="loading" class="progress mb-3" style="height: 4px">
            <div class="progress-bar progress-bar-striped progress-bar-animated w-100 bg-primary"></div>
          </div>

          <!-- Filtered results summary (desktop) -->
          <CCard class="mb-3 inv-summary-card d-none d-lg-block">
            <CCardBody class="p-3">
              <div class="d-flex align-items-center mb-3">
                <CIcon name="cil-calculator" class="mr-2 text-primary" />
                <span class="inv-summary-title">Summary of this page</span>
                <CBadge class="ml-2 badge-soft">{{ filteredSummary.totalCount }} quotation{{ filteredSummary.totalCount !== 1 ? 's' : '' }}</CBadge>
              </div>
              <CRow>
                <CCol lg="3" md="6" sm="6" class="mb-2">
                  <div class="inv-stat"><div class="inv-stat-label">Grand total</div><div class="inv-stat-val">RM {{ formatMyr(filteredSummary.totalAmount) }}</div></div>
                </CCol>
                <CCol lg="3" md="6" sm="6" class="mb-2">
                  <div class="inv-stat"><div class="inv-stat-label">Accepted</div><div class="inv-stat-val">RM {{ formatMyr(filteredSummary.acceptedAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.acceptedCount }} quotation{{ filteredSummary.acceptedCount !== 1 ? 's' : '' }}</div></div>
                </CCol>
                <CCol lg="3" md="6" sm="6" class="mb-2">
                  <div class="inv-stat"><div class="inv-stat-label">Sent, awaiting reply</div><div class="inv-stat-val">RM {{ formatMyr(filteredSummary.pendingAmount) }}</div><div class="inv-stat-meta">{{ filteredSummary.pendingCount }} quotation{{ filteredSummary.pendingCount !== 1 ? 's' : '' }}</div></div>
                </CCol>
                <CCol lg="3" md="6" sm="6" class="mb-2">
                  <div class="inv-stat"><div class="inv-stat-label">Average</div><div class="inv-stat-val">RM {{ formatMyr(filteredSummary.averageAmount) }}</div><div class="inv-stat-meta">per quotation</div></div>
                </CCol>
              </CRow>
            </CCardBody>
          </CCard>

          <!-- Desktop table -->
          <div class="d-none d-lg-block">
            <CDataTable
              :items="computedItems"
              :fields="fields"
              :sorter="{ external: true }"
              :table-filter="false"
              :items-per-page-select="{
                values: [10, 25, 50, 100],
                label: 'Items per page:'
              }"
              :items-per-page="perPage"
              hover
              striped
              bordered
              responsive
              @pagination-change="onPaginationChange"
              @sorter-value-change="onSorterChange"
            >
              <template #show_index="{ index }">
                <td class="py-2 text-muted">
                  {{ (currentPage - 1) * perPage + index + 1 }}
                </td>
              </template>

              <template #quotationNumber="{ item }">
                <td class="py-2">
                  <router-link
                    :to="`/tenants/Quotation/${item.id}`"
                    class="font-weight-bold text-monospace"
                  >
                    {{ item.quotationNumber }}
                  </router-link>
                </td>
              </template>

              <template #customerName="{ item }">
                <td class="py-2">
                  <div class="font-weight-medium">{{ item.customerName }}</div>
                  <small v-if="item.picName" class="text-muted">{{ item.picName }}</small>
                </td>
              </template>

              <template #displayIssuedDate="{ item }">
                <td class="py-2">{{ item.displayIssuedDate }}</td>
              </template>

              <template #grandTotal="{ item }">
                <td class="py-2 text-right">
                  <span class="font-weight-bold">RM {{ formatMyr(item.grandTotal) }}</span>
                </td>
              </template>

              <template #show_status="{ item }">
                <td class="py-2">
                  <CBadge
                    :color="badgeColor('quotation', item.statusDescription || 'Draft')"
                    class="px-2 py-1"
                  >
                    {{ badgeLabel("quotation", item.statusDescription || "Draft") }}
                  </CBadge>
                </td>
              </template>

              <template #show_details="{ item }">
                <td class="py-2 position-relative">
                  <div class="text-right action-buttons">
                    <CButtonGroup>
                      <CButton
                        color="light"
                        size="sm"
                        class="border"
                        :aria-label="`Edit quotation ${item.quotationNumber}`"
                        title="Edit"
                        @click="onEdit(item)"
                      >
                        <CIcon name="cil-pencil" />
                      </CButton>
                      <CDropdown
                        size="sm"
                        color="light"
                        placement="bottom-end"
                        class="dropdown-with-zindex border"
                      >
                        <template #toggler-content>
                          <CIcon name="cil-options" />
                          <span class="sr-only">More actions for {{ item.quotationNumber }}</span>
                        </template>
                        <CDropdownItem @click="onDuplicate(item)">
                          <CIcon name="cil-copy" class="mr-2" /> Duplicate
                        </CDropdownItem>
                        <CDropdownItem @click="convertToInvoice(item)">
                          <CIcon name="cil-file" class="mr-2" /> Convert to invoice
                        </CDropdownItem>
                        <CDropdownItem @click="downloadPDF(item)">
                          <CIcon name="cil-cloud-download" class="mr-2" /> Download PDF
                        </CDropdownItem>
                        <CDropdownDivider />
                        <CDropdownItem
                          class="text-danger"
                          @click="showDeleteConfirmation(item)"
                        >
                          <CIcon name="cil-trash" class="mr-2" /> Delete
                        </CDropdownItem>
                      </CDropdown>
                    </CButtonGroup>
                  </div>
                </td>
              </template>
            </CDataTable>
          </div>

          <!-- Mobile card list -->
          <div class="d-lg-none">
            <div class="d-flex align-items-center justify-content-between mb-3">
              <CSelect v-model="perPage" class="mb-0" @change="onMobilePerPage">
                <option :value="10">10 per page</option>
                <option :value="25">25 per page</option>
                <option :value="50">50 per page</option>
              </CSelect>
              <span class="text-muted small">
                {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }}
              </span>
            </div>

            <CCard
              v-for="item in computedItems"
              :key="item.id"
              class="mb-3"
            >
              <CCardBody class="py-3">
                <div class="d-flex justify-content-between align-items-start mb-2">
                  <div>
                    <router-link
                      :to="`/tenants/Quotation/${item.id}`"
                      class="font-weight-bold text-monospace"
                    >
                      {{ item.quotationNumber }}
                    </router-link>
                    <div class="small text-muted">{{ item.displayIssuedDate }}</div>
                  </div>
                  <CBadge
                    :color="badgeColor('quotation', item.statusDescription || 'Draft')"
                    class="px-2 py-1"
                  >
                    {{ badgeLabel("quotation", item.statusDescription || "Draft") }}
                  </CBadge>
                </div>
                <div class="d-flex justify-content-between align-items-end">
                  <div>
                    <div class="font-weight-medium">{{ item.customerName }}</div>
                    <div v-if="item.picName" class="small text-muted">{{ item.picName }}</div>
                  </div>
                  <div class="font-weight-bold text-right">RM {{ formatMyr(item.grandTotal) }}</div>
                </div>
              </CCardBody>
              <CCardFooter class="py-2 d-flex justify-content-between bg-transparent">
                <CButton color="light" class="border" @click="onEdit(item)">
                  <CIcon name="cil-pencil" /> Edit
                </CButton>
                <CDropdown color="light" placement="bottom-end" class="dropdown-mobile border">
                  <template #toggler-content>
                    <CIcon name="cil-options" /> More
                  </template>
                  <CDropdownItem @click="onDuplicate(item)">
                    <CIcon name="cil-copy" class="mr-2" /> Duplicate
                  </CDropdownItem>
                  <CDropdownItem @click="convertToInvoice(item)">
                    <CIcon name="cil-file" class="mr-2" /> Convert to invoice
                  </CDropdownItem>
                  <CDropdownItem @click="downloadPDF(item)">
                    <CIcon name="cil-cloud-download" class="mr-2" /> Download PDF
                  </CDropdownItem>
                  <CDropdownDivider />
                  <CDropdownItem class="text-danger" @click="showDeleteConfirmation(item)">
                    <CIcon name="cil-trash" class="mr-2" /> Delete
                  </CDropdownItem>
                </CDropdown>
              </CCardFooter>
            </CCard>
          </div>

          <!-- Pagination (server side) -->
          <div class="d-flex flex-wrap align-items-center justify-content-between mt-3">
            <small class="text-muted d-none d-lg-inline">
              {{ paginationStart }}–{{ paginationEnd }} of {{ totalItems }} quotations
            </small>
            <CPagination
              v-if="totalPages > 1"
              :activePage="currentPage"
              :pages="totalPages"
              size="sm"
              class="mb-0"
              @update:activePage="onPageChange"
            />
          </div>
        </template>
      </CCardBody>
    </CCard>

    <!-- Delete confirmation -->
    <CModal
      :title="`Delete quotation ${itemToDelete.quotationNumber || ''}?`"
      :show.sync="warningModal"
      :close-on-backdrop="!deleting"
      @update:show="onDeleteConfirmation"
    >
      <p class="mb-0">
        The quotation for <strong>{{ itemToDelete.customerName }}</strong>
        (RM {{ formatMyr(itemToDelete.grandTotal || 0) }}) will be removed.
        This can't be undone.
      </p>
      <template #footer>
        <CButton color="light" :disabled="deleting" @click="warningModal = false">
          Keep it
        </CButton>
        <CButton color="danger" :disabled="deleting" @click="confirmDelete">
          <CSpinner v-if="deleting" size="sm" class="mr-1" />
          Delete quotation
        </CButton>
      </template>
    </CModal>
  </div>
</template>

<script>
import QuotationApi from "@/lib/quotationApi";
import QuotationTrendChart from "../widgets/QuotationTrendChart";
import QuotationStatusChart from "../widgets/QuotationStatusChart";
import { formatMyr } from "@/utils/money";
import { formatDate, asDateString, mytToday } from "@/utils/datetime";
import { badgeColor, badgeLabel, badgeChartColor } from "@/utils/statusBadges";

const MONTH_LABELS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

// Last n calendar months in Malaysia time, oldest first: [{ key: "2026-10", label: "Oct" }]
function lastMonths(n) {
  const [y, m] = mytToday().split("-").map(Number);
  const out = [];
  for (let i = n - 1; i >= 0; i--) {
    const d = new Date(Date.UTC(y, m - 1 - i, 1));
    out.push({ key: d.toISOString().slice(0, 7), label: MONTH_LABELS[d.getUTCMonth()] });
  }
  return out;
}

const monthOf = (dt) => asDateString(dt).slice(0, 7);

export default {
  name: "QuotationList",
  components: {
    QuotationTrendChart,
    QuotationStatusChart,
  },
  data() {
    return {
      loading: true,
      loadError: false,
      deleting: false,
      items: [],
      fields: [
        {
          key: "show_index",
          label: "#",
          _style: "width:50px",
          sorter: false,
          filter: false,
        },
        {
          key: "quotationNumber",
          label: "Quotation no.",
          sorter: true,
          filter: true,
        },
        {
          key: "customerName",
          label: "Customer",
          sorter: true,
          filter: true,
        },
        {
          key: "displayIssuedDate",
          label: "Issued",
          sorter: true,
          filter: true,
        },
        {
          key: "grandTotal",
          label: "Amount",
          sorter: true,
          filter: false,
          _style: "text-align: right",
        },
        {
          key: "show_status",
          label: "Status",
          sorter: true,
          filter: true,
        },
        {
          key: "show_details",
          label: "Actions",
          _style: "width:110px",
          sorter: false,
          filter: false,
        },
      ],
      api: new QuotationApi(),
      warningModal: false,
      itemToDelete: {},
      currentPage: 1,
      perPage: 10,
      totalItems: 0,
      totalPages: 1,
      // Sorting
      sortBy: "issuedDate",
      sortDirection: "desc",
      // Filters
      showFilters: false,
      filters: {
        quotationNumber: "",
        customerName: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      },
      // Real analytics source — all quotations for org-wide summary
      allQuotations: [],
      statusOptions: [],
    };
  },
  mounted() {
    this.$store.commit("setPageNavItems", ["Sales", "Quotations"]);
    this.loadStatusOptions();
    this.refreshTable();
    this.fetchSummary();
  },
  computed: {
    computedItems() {
      return this.items.map((item) => {
        return {
          ...item,
          displayIssuedDate: formatDate(item.issuedDate),
          customerName: this.getCustomerName(item),
          picName: this.getPicName(item),
          grandTotal: this.getGrandTotal(item),
        };
      });
    },
    paginationStart() {
      if (this.totalItems === 0) return 0;
      return (this.currentPage - 1) * this.perPage + 1;
    },
    paginationEnd() {
      return Math.min(this.currentPage * this.perPage, this.totalItems);
    },
    hasActiveFilters() {
      return Object.values(this.filters).some((filter) => filter !== "");
    },
    activeFiltersCount() {
      return Object.values(this.filters).filter((filter) => filter !== "").length;
    },
    dateRangeInvalid() {
      return !!(this.filters.dateFrom && this.filters.dateTo && this.filters.dateTo < this.filters.dateFrom);
    },
    // Summary of the rows on the current page
    filteredSummary() {
      const quotations = this.computedItems;
      let totalAmount = 0;
      let acceptedAmount = 0;
      let pendingAmount = 0;
      let acceptedCount = 0;
      let pendingCount = 0;

      quotations.forEach((q) => {
        const amount = parseFloat(q.grandTotal) || 0;
        totalAmount += amount;
        if (q.statusDescription === "Accepted") {
          acceptedAmount += amount;
          acceptedCount++;
        } else if (q.statusDescription === "Sent") {
          pendingAmount += amount;
          pendingCount++;
        }
      });

      const averageAmount = quotations.length > 0 ? totalAmount / quotations.length : 0;
      return {
        totalAmount,
        acceptedAmount,
        pendingAmount,
        acceptedCount,
        pendingCount,
        averageAmount,
        totalCount: quotations.length,
      };
    },
    // ===== Real analytics (computed from allQuotations) =====
    quotationSummary() {
      const all = this.allQuotations || [];
      let acceptedAmount = 0, acceptedCount = 0, pendingAmount = 0, pendingCount = 0, rejectedAmount = 0, rejectedCount = 0;
      all.forEach((q) => {
        const amt = this.getGrandTotal(q);
        const s = q.statusDescription;
        if (s === "Accepted") { acceptedAmount += amt; acceptedCount++; }
        else if (s === "Sent") { pendingAmount += amt; pendingCount++; }
        else if (s === "Rejected") { rejectedAmount += amt; rejectedCount++; }
      });
      const [prev, current] = lastMonths(2);
      const inMonth = (key) => all.filter((q) => monthOf(q.issuedDate) === key).length;
      const cThis = inMonth(current.key), cLast = inMonth(prev.key);
      const countChange = cLast ? Math.round(((cThis - cLast) / cLast) * 1000) / 10 : (cThis > 0 ? 100 : 0);
      return { totalCount: all.length, countChange, acceptedAmount, acceptedCount, pendingAmount, pendingCount, rejectedAmount, rejectedCount };
    },

    statusDistribution() {
      const all = this.allQuotations || [];
      const counts = {};
      all.forEach((q) => { const s = q.statusDescription || "Draft"; counts[s] = (counts[s] || 0) + 1; });
      const total = all.length || 1;
      return Object.keys(counts).map((name) => ({ name, count: counts[name], percentage: Math.round((counts[name] / total) * 1000) / 10 }));
    },

    // Last 6 months: all quotations vs accepted (real data)
    quotationTrendData() {
      const months = lastMonths(6);
      const all = this.allQuotations || [];
      const sumFor = (key, onlyAccepted) =>
        all
          .filter((q) => monthOf(q.issuedDate) === key && (!onlyAccepted || q.statusDescription === "Accepted"))
          .reduce((a, q) => a + this.getGrandTotal(q), 0);
      const lineColor = badgeChartColor("invoice", "Approve"); // primary
      const acceptedColor = badgeChartColor("quotation", "Accepted"); // success
      return {
        labels: months.map((m) => m.label),
        datasets: [
          { label: "Quotations", backgroundColor: "transparent", borderColor: lineColor, pointBackgroundColor: lineColor, data: months.map((m) => sumFor(m.key, false)) },
          { label: "Accepted", backgroundColor: "transparent", borderColor: acceptedColor, pointBackgroundColor: acceptedColor, data: months.map((m) => sumFor(m.key, true)) },
        ],
      };
    },
    statusDistributionData() {
      return {
        labels: this.statusDistribution.map((status) => badgeLabel("quotation", status.name)),
        datasets: [
          {
            data: this.statusDistribution.map((status) => status.count),
            backgroundColor: this.statusDistribution.map((status) =>
              badgeChartColor("quotation", status.name)
            ),
          },
        ],
      };
    },
  },
  methods: {
    formatMyr,
    badgeColor,
    badgeLabel,
    getPicName(item) {
      if (item.contactName) return item.contactName;
      if (item.customer && item.customer.contactName) return item.customer.contactName;
      return "";
    },
    getCustomerName(item) {
      if (item.customer && item.customer.name) return item.customer.name;
      return "No customer";
    },
    getGrandTotal(quotation) {
      var total = 0;
      if (quotation.items && quotation.items.length > 0) {
        for (var i = 0; i < quotation.items.length; i++) {
          var item = quotation.items[i];
          total += (item.price || 0) * (item.quantity || 0);
        }
      }
      return total;
    },
    // Load org-wide quotations for real analytics (KPIs, distribution, trend)
    fetchSummary() {
      this.api
        .getListByCurrentBusiness({ page: 1, pageSize: 1000 })
        .then((r) => {
          this.allQuotations = (r && (r.data || (r.result && (r.result.data || r.result)))) || [];
        })
        .catch(() => { /* analytics degrade gracefully; the table still works */ });
    },
    refreshAll() {
      this.refreshTable();
      this.fetchSummary();
    },
    refreshTable() {
      var self = this;
      self.loading = true;
      self.loadError = false;

      const params = {
        page: self.currentPage,
        pageSize: self.perPage,
        sortBy: self.sortBy,
        sortDirection: self.sortDirection,
      };
      if (self.filters.quotationNumber) params.quotationNumber = self.filters.quotationNumber;
      if (self.filters.customerName) params.customerName = self.filters.customerName;
      if (self.filters.status) params.status = self.filters.status;
      if (self.filters.dateFrom) params.dateFrom = self.filters.dateFrom;
      if (self.filters.dateTo) params.dateTo = self.filters.dateTo;

      self.api
        .getListByCurrentBusiness(params)
        .then((response) => {
          if (response && response.data) {
            self.items = response.data || [];
            self.totalItems = response.totalRecords || 0;
            self.currentPage = response.page || 1;
            self.totalPages = response.totalPages || 1;
          } else if (response.result && response.result.data) {
            self.items = response.result.data || [];
            self.totalItems = response.result.totalRecords || 0;
            self.currentPage = response.result.page || 1;
            self.totalPages = response.result.totalPages || 1;
          } else {
            self.items = response.result || response || [];
            self.totalItems = self.items.length;
            self.totalPages = Math.ceil(self.totalItems / self.perPage);
          }
          if (self.totalItems > 0 && self.totalPages < 1) {
            self.totalPages = 1;
          }
        })
        .catch((error) => {
          console.error("Failed to load quotations", error);
          self.loadError = true;
        })
        .finally(() => {
          self.loading = false;
        });
    },
    onDuplicate(item) {
      this.$router.push({ path: `/tenants/Quotation/${item.id}/duplicate` });
    },
    onEdit(item) {
      this.$router.push({ path: `/tenants/Quotation/${item.id}` });
    },
    showDeleteConfirmation(item) {
      this.itemToDelete = item;
      this.warningModal = true;
    },
    confirmDelete() {
      if (this.deleting) return;
      this.deleting = true;
      const number = this.itemToDelete.quotationNumber;
      this.api
        .delete(this.itemToDelete.id)
        .then(() => {
          this.warningModal = false;
          this.refreshAll();
          this.$toast.success(number ? `Quotation ${number} deleted` : "Quotation deleted");
        })
        .catch(({ data } = {}) => {
          this.$toast.error(`Couldn't delete the quotation. ${helper.getErrorMessage(data)}`);
        })
        .finally(() => {
          this.deleting = false;
        });
    },
    onDeleteConfirmation(status) {
      if (!status) {
        this.itemToDelete = {};
      }
    },
    addNew() {
      this.$router.push({ path: "/tenants/Quotation" });
    },
    loadStatusOptions() {
      this.api
        .getQuotationStatus()
        .then((response) => {
          this.statusOptions = response.result;
        })
        .catch(() => {
          // Fall back to the backend enum if the lookup fails
          this.statusOptions = [
            { id: 0, name: "Draft" },
            { id: 1, name: "Sent" },
            { id: 2, name: "Accepted" },
            { id: 3, name: "Rejected" },
            { id: 4, name: "Cancelled" },
          ];
        });
    },
    // CDataTable emits the new page size here
    onPaginationChange(itemsPerPage) {
      const size = parseInt(itemsPerPage);
      if (size && size !== this.perPage) {
        this.perPage = size;
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onMobilePerPage(evt) {
      const size = parseInt(evt && evt.target ? evt.target.value : evt);
      if (size) {
        this.perPage = size;
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    onPageChange(page) {
      if (page !== this.currentPage) {
        this.currentPage = page;
        this.refreshTable();
      }
    },
    onSorterChange(sorter) {
      if (sorter && sorter.column) {
        this.sortBy = sorter.column;
        this.sortDirection = sorter.asc ? "asc" : "desc";
        this.currentPage = 1;
        this.refreshTable();
      }
    },
    resetFilters() {
      this.filters = {
        quotationNumber: "",
        customerName: "",
        status: "",
        dateFrom: "",
        dateTo: "",
      };
      this.currentPage = 1;
      this.refreshTable();
    },
    applyFilters() {
      if (this.dateRangeInvalid) return;
      this.currentPage = 1;
      this.refreshTable();
    },
    convertToInvoice(item) {
      this.$router.push({ path: `/tenants/invoice/${item.id}/convertFromQuot` });
    },
    downloadPDF(item) {
      const name = item.quotationNumber ? `${item.quotationNumber}.pdf` : undefined;
      this.api.downloadPDF(item.id, name).catch(() => {
        this.$toast.error("Couldn't download the PDF. Check your connection and try again.");
      });
    },
  },
};
</script>

<style scoped>
/* Keep the row's dropdown above neighbouring rows */
.dropdown-with-zindex {
  position: static;
}

.dropdown-with-zindex .dropdown-menu {
  z-index: 1050 !important;
  position: absolute;
  will-change: transform;
}

.dropdown-mobile .dropdown-menu {
  z-index: 1055 !important;
  position: absolute;
  will-change: transform;
}

.action-buttons,
.action-buttons .btn-group {
  position: relative;
  z-index: auto;
}

.action-buttons:has(.dropdown.show),
.action-buttons .btn-group:has(.dropdown.show) {
  z-index: 1060;
}

.status-dot {
  display: inline-block;
  width: 12px;
  height: 12px;
  border-radius: 50%;
}

/* KPI tiles */
.inv-kpi {
  background: #fff;
  border: 1px solid #e2e8f0;
  border-radius: 14px;
  padding: 1.1rem 1.15rem;
  height: 100%;
}
.inv-kpi-top { display: flex; align-items: center; justify-content: space-between; margin-bottom: 0.75rem; }
.inv-kpi-label { font-size: 0.8rem; font-weight: 600; color: #64748b; }
.inv-kpi-chip { width: 34px; height: 34px; border-radius: 9px; display: grid; place-items: center; font-size: 0.95rem; }
.chip-navy { background: rgba(30, 58, 95, 0.1); color: #1e3a5f; }
.chip-success { background: rgba(16, 185, 129, 0.13); color: #059669; }
.chip-warning { background: rgba(245, 158, 11, 0.14); color: #b45309; }
.chip-danger { background: rgba(239, 68, 68, 0.11); color: #ef4444; }
.inv-kpi-value { font-size: 1.5rem; font-weight: 800; color: #1e3a5f; line-height: 1.1; }
.inv-kpi-sub { font-size: 0.74rem; color: #64748b; margin-top: 0.35rem; }
.inv-kpi-sub.trend-up { color: #059669; }
.inv-kpi-sub.trend-down { color: #ef4444; }

/* Page summary */
.inv-summary-card { border: 1px solid #e2e8f0; border-radius: 14px; box-shadow: none; }
.inv-summary-title { font-size: 0.85rem; font-weight: 700; color: #1e3a5f; }
.badge-soft { background: #eef2f7; color: #1e3a5f; font-weight: 600; }
.inv-stat { background: #f8fafc; border: 1px solid #eef2f7; border-radius: 10px; padding: 0.6rem 0.7rem; height: 100%; }
.inv-stat-label { font-size: 0.75rem; color: #64748b; }
.inv-stat-val { font-size: 0.95rem; font-weight: 700; color: #334155; }
.inv-stat-meta { font-size: 0.7rem; color: #64748b; }

::v-deep .table tbody td { vertical-align: middle; }
</style>
